import {
  CheckoutRules,
  CheckoutRuleDocumentType,
  SessionNotificationsArray,
} from '../../../interfaces/config'
import { Nullable } from '../../commons/types'
import { ICheckoutRuleAttributes } from '../getCheckoutRule/types'

export interface ISessionVariables {
  storefrontName?: string
  sessionUid?: string
}

export enum RecommendedProductsValues {
  ML = 'ML',
  NONE = 'NONE',
}

export enum PrefillCartValues {
  LAST_ORDER = 'LAST_ORDER',
  ML = 'ML',
  NONE = 'NONE',
}

export enum WorkflowChannel {
  WHATSAPP = 'whatsapp',
  LINE = 'line',
}

interface WorkflowDictionary
  extends Record<
    string,
    {
      name?: string
      channel?: `${WorkflowChannel}`
      channelUid?: string
      stephook?: any
      jwtToken?: string
      body?: any
      ng: boolean
    }
  > {}

export interface CheckoutRulesFields {
  quantity: number
  message: string
  isActive: boolean
}

interface ProductCheckoutRules extends CheckoutRulesFields {
  sku: string[]
}

interface StockConfig {
  id: string
  warningMessage: string
  quantity: number
  enabled: boolean
}

export enum CheckoutRulesKeys {
  cartConditionPendingMinAmount = 'cartConditionPendingMinAmount',
  cartConditionPendingMaxAmount = 'cartConditionPendingMaxAmount',
  cartConditionPendingMinQty = 'cartConditionPendingMinQty',
  cartConditionPendingMaxQty = 'cartConditionPendingMaxQty',
  cartConditionPendingUserValidation = 'cartConditionPendingUserValidation',
  cartWarningCheckReturnables = 'cartWarningCheckReturnables',
}

/**
 * Async order-confirmation parameters for a storefront.
 *
 * Reached as `configuration.order.confirmation`.
 *
 * ⚠️ Under `order`, which is a **sibling** of `checkoutRules` and never a
 * member of it. The checkout-rule map is a closed set of keys, each with a
 * handler; adding an unhandled key to it disables that storefront's rule
 * enforcement outright (COM-736).
 *
 * Authored per storefront on the session config. Absent for every storefront
 * that has not opted in, which is the common case and must stay the default —
 * and absent there means the whole `order` node is missing, not merely this
 * block, so read it through both optional hops.
 * @internal
 */
export interface IOrderConfirmationConfig {
  /**
   * Whether this storefront confirms orders asynchronously.
   *
   * The switch for the Order Detail screen's confirming/timeout states: only
   * `true` turns them on. Remote JSON, so it is compared against `true` and
   * not merely truthy — a quoted `"false"` must not opt a storefront in.
   */
  required?: boolean
  /**
   * `OrderStatus` values that count as a settled, successful order.
   *
   * Read by `deriveOrderOutcome`, which is what the in-webview confirmation
   * flow and its screen both use to decide an order has settled. Defaults to
   * `DEFAULT_SUCCESS_STATUSES` (`['CONFIRMED']`); 13 `OrderStatus` members
   * exist and tenants settle on different ones, which is why it is
   * configurable at all.
   *
   * It is **not** read by `isOrderPending`, and must not become so. That
   * predicate asks whether the backend is still working on the order; this
   * list says which statuses are final. A bare `CREATED` order is not pending
   * — it may be one nothing will ever confirm — and making it so would poll to
   * the deadline at the cost of a `completeBundlesExtra` lookup per tick.
   *
   * Members are validated against the enum by `getUnknownOrderStatuses`: a
   * typo here is a status nothing can ever match, so the order polls out and
   * reports a timeout it never had.
   *
   * Has no effect on the Order Detail screen, which shows an order that
   * already exists and derives its pending affordance from `isOrderPending`
   * alone.
   */
  successStatuses?: string[]
  /**
   * `OrderStatus` values that count as a settled, failed order.
   *
   * As `successStatuses`, and checked ahead of it, so a status listed under
   * both resolves as a failure. Defaults to `DEFAULT_FAILURE_STATUSES`
   * (`['ERROR', 'CANCELLED']`).
   */
  failureStatuses?: string[]
  /**
   * Milliseconds between poll ticks, **from the second look on**.
   *
   * This does not govern how long the shopper waits before anything happens.
   * The look that *opens* a poll chain is fixed at `ORDER_POLL_FIRST_LOOK_MS`
   * (500 ms, `redux/slices/orders/types.ts`) and is not configurable, so
   * lowering this will not shorten the spinner — it only makes a genuinely
   * slow confirmation re-read more often, and every tick costs a
   * `completeBundlesExtra` lookup server-side (COM-875). (A refresh of an
   * order the screen is already polling re-reads immediately instead, so it
   * has no opening delay at all.)
   *
   * Two different wrong values fail two different ways, and `pollingInterval`
   * in `utils/orderConfirmation.ts` draws the distinction on purpose. A
   * *positive* value below `ORDER_POLL_MIN_INTERVAL_MS` (1000) is a real
   * instruction and is raised to that floor — `5` resolves to 1000. A value
   * that is not positive at all (`0`, a negative, or an omitted key arriving
   * as `null`) is read as unconfigured and falls back to
   * `ORDER_POLL_INTERVAL_MS` instead — `0` resolves to **5000**, five times
   * the floor and in the opposite direction from what an author lowering it
   * intends. Either way the first look stays at 500 ms. COM-871 covers the
   * missing report when a value is clamped or ignored.
   */
  pollingTime?: number
  /**
   * Maximum number of poll ticks — a ceiling you can only ever **lower**.
   *
   * The value in force is `min(authored, afforded)`, where `afforded` is what
   * the deadline pays for at the resolved cadence:
   * `ceil((timeout - 500) / pollingTime)`, the grid opening at
   * `ORDER_POLL_FIRST_LOOK_MS` rather than at one whole interval. So an
   * authored value **below** `afforded` is a real, tighter instruction and is
   * honoured exactly; one **above** it runs on `afforded` and is reported
   * `clamped`, because it was never reachable — the deadline ended the chain
   * first either way, and the key was dead configuration that read like a
   * longer poll.
   *
   * **The lever for a longer poll is `timeout`, not this.** Raising this alone
   * buys nothing at all; raising `timeout` raises both, up to its own ceiling.
   *
   * `0` or a negative is `rejected` and the derived ceiling applies. `0` is
   * worth naming separately because joi permits it deliberately server-side,
   * so it is a value that genuinely arrives rather than a defensive branch.
   *
   * Unconfigured — which includes the `null` an omitted key arrives as — is
   * that same ceiling and not `Infinity`: since COM-871 an unconfigured chain
   * stops on the last tick that fits inside its deadline instead of on the
   * first one past it.
   */
  maxAttempts?: number
  /**
   * Milliseconds of wall clock before the client gives up, **bounded at both
   * ends**.
   *
   * The one authored budget, and the number every other bound is read off, so
   * it is the only key that can lengthen a poll — and, for that reason, the
   * one that has to be bounded itself.
   *
   * - **At or below `ORDER_POLL_FIRST_LOOK_MS` (500) it is `rejected`** and
   *   `ORDER_POLL_TIMEOUT_MS` (60000) applies instead. A budget that expires
   *   before the opening look buys no observation at all, so every order on
   *   such a storefront reported "outcome unknown" however fast it confirmed,
   *   including ones that settled in three seconds. There is no honourable
   *   approximation of "give up before looking", which is why this end is a
   *   rejection rather than a clamp.
   * - **Above `ORDER_POLL_MAX_TIMEOUT_MS` (600000 — ten minutes) it is
   *   `clamped`** to that value, and the report says the authored value was
   *   legitimate and overridden by policy rather than wrong. An order that has
   *   not settled in ten minutes will not settle while the shopper is
   *   watching, and each further tick costs a `completeBundlesExtra` lookup
   *   server-side. The ceiling caps a chain at 601 requests (600 grid ticks
   *   plus the closing look on the deadline), at the interval
   *   floor; without it `timeout: 86400000` with `pollingTime: 1000` was
   *   86,400 of them, spread over a day, for one abandoned checkout.
   * - `0` or a negative is `rejected` for the reason `maxAttempts` gives, and
   *   `0` likewise arrives in earnest because joi permits it.
   *
   * Anything between the two is honoured exactly. The low boundary is
   * exclusive and the high one inclusive: 500 is rejected, 501 honoured,
   * 600000 honoured, 600001 clamped.
   */
  timeout?: number
}

export interface ICheckoutRules {
  [CheckoutRulesKeys.cartConditionPendingMinAmount]: CheckoutRulesFields
  [CheckoutRulesKeys.cartConditionPendingMaxAmount]: CheckoutRulesFields
  [CheckoutRulesKeys.cartConditionPendingMinQty]: CheckoutRulesFields
  [CheckoutRulesKeys.cartConditionPendingMaxQty]: CheckoutRulesFields
  [CheckoutRulesKeys.cartConditionPendingUserValidation]: ProductCheckoutRules
  [CheckoutRulesKeys.cartWarningCheckReturnables]: ProductCheckoutRules
}

/**
 * Enum representing the types of quantity adjustment that can be applied during cart or order processing.
 *
 * - NONE: No adjustment is performed; quantities are used as provided.
 * - VALIDATE: Quantity is validated against certain criteria (such as stock or business rules), but not changed.
 * - ROUND: Quantity may be rounded according to pallets, layers, jumpQty, or other constraints.
 */
export enum QuantityAdjustment {
  /**
   * No adjustments applied to the quantity.
   */
  NONE = 'NONE',

  /**
   * Validate the quantity, but do not change it.
   */
  VALIDATE = 'VALIDATE',

  /**
   * Round the quantity as per applicable rules or constraints.
   */
  ROUND = 'ROUND',
}

/**
 * How the backend should respond when the `GET_CHECKOUT_RULE` add-on fails
 * for a rule (COM-1077). Yao's §9: tenant-level dial between blocking
 * checkout and falling back to the storefront rule.
 *
 * `'error'` is today's behaviour and the backend's default — the shopper
 * cannot order while the add-on is down. `'storefront'` falls back to the
 * authored `paymentRules.default` / `dateRules` list, which may be stale.
 * `'empty'` offers nothing and lets the recovery hook route accordingly.
 *
 * Values the backend writes server-side; cinnamon reads but does not
 * validate them. A storefront that author an unknown value is a backend
 * problem to surface, not a cinnamon one — joi rejects anything else at the
 * admin API.
 */
export type OnCheckoutRuleAddonFailure = 'error' | 'storefront' | 'empty'

/**
 * Per-rule-type tenant settings on the cart (COM-1070). Yao's §5 and his
 * `getCheckoutRule` guide (table following §7): one object per document-rule
 * type that cinnamon serves (`DELIVERY_DATES`, `PAYMENT_METHODS`), each
 * optional and defaulting to today's behaviour.
 *
 * Every field is optional on the object and the object itself is optional —
 * the backend writes nothing a tenant did not author, so an absent key
 * everywhere up the chain is the common case. Yao's §5: "an absent object,
 * rule type or field means today's behaviour".
 */
/**
 * ⚠️ **Every optional field carries `| null`.** GraphQL sends `null` for a
 * selected field a tenant did not author, not `undefined` — so a tenant that
 * authored only `PAYMENT_METHODS: { required: true }` ships
 * `{ DELIVERY_DATES: null, PAYMENT_METHODS: { required: true, onAddonFailure:
 * null } }` on the wire. A consumer branching on `=== undefined` to apply the
 * `'error'` default would then take the wrong branch, and the compiler could
 * not catch it. Review of #813 (2026-10-06).
 */
export interface ICheckoutRuleSetting {
  /**
   * Whether the shopper must pick a value before `createOrder` succeeds
   * (COM-1081). The backend enforces it server-side; cinnamon reads it to
   * tune the queue COM-1046 walks and the dialog COM-1047 shows. Omitted
   * **or `null`** means `false` — today's behaviour — so no storefront that
   * has not opted in sees a change.
   */
  required?: boolean | null
  /**
   * How the backend handles a `GET_CHECKOUT_RULE` add-on failure for this
   * rule type. Omitted **or `null`** means `'error'` — today's behaviour —
   * so a storefront that has not opted in blocks checkout on an add-on
   * outage, as it does today.
   */
  onAddonFailure?: OnCheckoutRuleAddonFailure | null
}

/**
 * `configuration.cart.checkoutRuleSettings` as the user API shapes it.
 *
 * Keyed on the two document-rule types cinnamon serves — derived from
 * `CheckoutRuleDocumentType` via `Partial<Record<…>>` so a change to the
 * union propagates here rather than needing a hand edit (review of #813,
 * 2026-10-06). A rule-type key may be missing (opt in for payment only) or
 * **`null`** (tenant authored the key with no fields underneath); both read
 * as "today's behaviour for this rule". The backend's SDL types this as an
 * object with named sub-fields, so an unknown rule name is a joi-rejected
 * authoring mistake rather than a shape cinnamon encounters.
 */
export type ICheckoutRuleSettings = Partial<
  Record<CheckoutRuleDocumentType, ICheckoutRuleSetting | null>
>

interface ISessionCart {
  quantityAdjustment: `${QuantityAdjustment}`
  /**
   * Per-rule-type tenant settings for M2 (COM-1070). Optional on the object
   * because a storefront that has not opted in sends no `checkoutRuleSettings`
   * at all — the user API returns `null` and the slice selector reads
   * `undefined` for every rule. The storefront's production rollout of
   * COM-1070 landed 2026-10-07, so nothing here is gated now. The standing
   * rollout rule for the next field added beside this one is stated on the
   * `order { confirmation }` block in `../query.ts` (dedupe at review of
   * #813, 2026-10-06, rewritten at review of COM-1195, 2026-10-07).
   */
  checkoutRuleSettings?: ICheckoutRuleSettings | null
}

interface SessionConfiguration {
  stock?: {
    hideOutOfStock?: boolean
    enabled?: boolean
    stockThreshold?: number
    key?: string
    threshold: {
      criticalLowWarning: StockConfig
      lowWarning: StockConfig
    }
  }
  i18n?: {
    language?: string
    currencyFormat?: string
    currencySymbol?: string
    currencyCode?: string
    country?: string
    /**
     * IANA time-zone name the storefront's own dates are expressed in.
     *
     * **Nothing reads this yet.** It is selected so `getOrderDetailPanelProps`
     * can forward it as the panel's `timeZone`, making an order's *timestamp*
     * render in the tenant's zone rather than the viewer's device's. That
     * forwarding lands with the `1.20.0` bump, because the panel does not
     * declare the prop before then (COM-816).
     *
     * It is **not** what fixes a delivery date. That field names a calendar day
     * and the library renders it as stored, in UTC, via
     * `formatOrderCalendarDay` — which takes no zone at all, precisely so this
     * cannot be got wrong. The defect it guards against is reaching for
     * `formatOrderDate` with a tenant zone for a delivery date, which moves it
     * to the previous day west of UTC (COM-816).
     *
     * Not to be confused with the `dateRules.timezone` the delivery-date engine
     * reads. That one lives on the storefront's **checkout-rule documents** (the
     * `CheckoutRules` model, read at
     * `storefront/packages/graphql-schemas/src/user/lib/checkoutRules/lib/dateRule.ts`),
     * *not* on `configuration.checkoutRules` — which is a sibling field on this
     * interface and has no `dateRules` member at all. Two different entities,
     * two zones, and nothing constrains them to agree.
     *
     * Typed `| null` because the field is nullable server-side and is now
     * *selected*: graphql returns an authored-but-empty field as `null`, not as
     * an absent key, so `undefined` alone could not hold the value the server
     * actually sends. The `?` is not redundant with it — `SessionDataArgs` is
     * also built as an object literal in half a dozen test files, which would
     * all have to name this field without it.
     *
     * Flatten the `null` before it reaches the panel: the prop is
     * `timeZone?: string`, and `Intl` throws on a `null` zone where it ignores
     * an absent one. `optional()` in `utils/orderDetailPanel.ts` is the helper,
     * though note it is currently used only by the order mappers in that file,
     * not yet by the props adapter.
     *
     * The five siblings above are equally nullable on the schema and equally
     * selected, and are typed `?: string` — they are under-typed, not this one.
     * Left alone deliberately: widening them is unrelated to COM-816 and would
     * put six wire-shape changes in a PR about one.
     */
    timezone?: string | null
  }
  workflow?: WorkflowDictionary
  /**
   * Server-side order splitting, read only to refuse a path it forbids.
   *
   * Cinnamon does not split anything and has no business knowing how orders
   * are chunked. It is selected because `createOrder` and `confirmOrder` both
   * `throw ERROR.BAD_REQUEST` outright when `isOrderSplit(session)` is true —
   * `'please call to the mutation "createOrders" to create a split order'` —
   * and `useCreateOrder`'s in-webview branch calls exactly those two
   * mutations. Without this field that branch would 400 on every attempt for
   * such a storefront and report a generic creation failure, with the real
   * cause (`inWebviewOrderConfirmation` authored on a splitting storefront)
   * appearing nowhere.
   *
   * The predicate mirrors `isOrderSplit`: active **and** a non-zero
   * `maxProduct`, because a splitter authored with `maxProduct: 0` splits
   * nothing and the server treats it as off.
   */
  splitters?: {
    order?: {
      active?: boolean
      maxProduct?: number
    }
  }
  checkoutRules?: ICheckoutRules
  /**
   * Order-level configuration. A sibling of `checkoutRules`, never a member of
   * it — see `IOrderConfirmationConfig`.
   *
   * A namespace rather than a flat `orderConfirmation` key, so later
   * order-scoped settings have somewhere to go without another top-level
   * member on `configuration`. Nothing shares it yet, which is why
   * `confirmation` is its only field.
   *
   * Not to be confused with `splitters.order` or `groupers.order`, which are
   * members of their own blocks and unrelated to this one.
   *
   * **Both hops are declared optional, but the non-opted-in storefront does
   * not send an absent `order` — it sends a present one with a `null`
   * child.** The shape depends on how the document was read, and the path
   * this repo is on is the hydrated one:
   *
   * - `configuration.order` is a Mongoose **nested path** (a plain object
   *   literal in the schema), not a single-nested subdocument. Mongoose
   *   materialises a nested path as `{}` on a hydrated document even when
   *   nothing under it was ever authored, while `confirmation` — a real
   *   `Schema` instance — stays `undefined`.
   * - The user API's `session` query returns a **hydrated** document:
   *   `GenericModel.get` is `findOne()` with no `.lean()`. Through GraphQL's
   *   default resolver the truthy `{}` is a live object, so its sub-selection
   *   executes and the response carries `order: { confirmation: null }`.
   * - A **lean** read is the other shape: the nested path is `undefined`
   *   there, so it emits `order: null`.
   *
   * Verified against Mongoose 7.8.11 and graphql-js 16.13.2 rather than
   * inferred. So on the boot response, for every storefront that has not
   * opted in — which is all of them today — the `order` node **is present**.
   *
   * Chaining through both hops (`configuration?.order?.confirmation`) is
   * therefore still required, but for the `null`, not for an absent node.
   * What must not be written is a truthiness test on the node itself: a
   * future `if (configuration.order)` read as an opt-in test reads as opted-in
   * fleet-wide. Dropping the second `?.` is a narrower hazard than it looks —
   * `configuration?.order.confirmation` would throw on a lean read, but
   * `order?:` is optional under `strict: true`, so it does not compile and
   * cannot reach production; the tests that assert the throw
   * (`utils/tests/orderDetailPanel.test.ts`) mutate the chain deliberately.
   *
   * `resolveOrderConfirmation` treats a null-or-absent `confirmation`, a
   * null-or-absent `order` and an absent `configuration` identically, all of
   * them resolving to COM-741's constants — with the one exception COM-871
   * introduced. That holds for four fields of five: `maxAttempts` resolves to
   * the ceiling derived from the other two rather than to `Infinity`, so an
   * unconfigured chain stops on the last tick inside its deadline instead of
   * on the first one past it.
   *
   * ⚠️ **This type cannot express the shape above**, which is why the
   * mismatch went unnoticed: both hops are `?:`-optional and neither is
   * `| null`, so under `strictNullChecks` no fixture can encode the hydrated
   * response. Widening the outer hop alone typechecks clean but only buys the
   * lean shape, which this query never returns; widening `confirmation` is
   * the half that matters and breaks 11 call sites across the four read sites
   * plus `resolveOrderConfirmation`'s own parameter. That makes it a change
   * with its own tests rather than a comment fix, and it is left to its own
   * ticket. `timezone?: string | null` in this file is the precedent for the
   * eventual shape.
   */
  order?: {
    /** Async order-confirmation parameters — see `IOrderConfirmationConfig`. */
    confirmation?: IOrderConfirmationConfig
  }
  groupers?: {
    cart: {
      active: boolean
      session: string[]
      product: string[]
    }
    order: {
      active: boolean
      session: string[]
      product: string[]
    }
  }
  showRecommendations?: boolean
  filters?: {
    attributes: string[]
    promotions: {
      active?: boolean
    }
    categories: {
      active?: boolean
    }
    brands: {
      active?: boolean
    }
  }
  promotions?: {
    autoAdd: boolean
    disableFreeGoodsAsDiscount: boolean | null
  }
  recommendedProducts?: {
    prefillCart: PrefillCartValues
    frequentlyBoughtTogether: RecommendedProductsValues
    suggestedItems: RecommendedProductsValues
  }
  notifications?: SessionNotificationsArray
  cart: Nullable<ISessionCart>
}

export interface Banner {
  id: string
  imagesURL: string[]
  customerType: string
  reference: string
  type: string
  isActive: boolean
  priority: number
  startDate: Date
  expirationDate: Date
  userAgent: string | null
}

export interface Customer {
  email?: string
  phoneNumber?: string[]
  code?: string
  type?: string
  customFields?: Record<string, unknown>
}

export type extraId = {
  key: string
  value: string
}

/**
 * The shopper's current checkout-rule selections, keyed by `ruleType`.
 *
 * ⚠️ Confusable with `Session.configuration.checkoutRules`, and the two
 * cannot be mixed:
 * - **This** array is the sibling `Session.checkoutRules`, the *selections*
 *   the shopper (or the storefront's addons) has made for `DELIVERY_DATES`
 *   and `PAYMENT_METHODS`. Written by `Session.setCheckoutRuleById`, mirrored
 *   onto the order at create time.
 * - The map at `Session.configuration.checkoutRules` (`ICheckoutRules`) is
 *   the closed *validation-rule* set (`cartConditionPending*`, etc.) whose
 *   keys each need a handler; adding an unknown key there silently disables
 *   that storefront's rule enforcement (COM-736).
 *
 * `setCheckoutRuleById` replaces rather than accumulates — the array holds
 * at most one entry per `ruleType`. Both entries may be absent, and that is
 * the pilot storefront's normal state until the write path exists (OQ-7 on
 * COM-849). The cart's `CartCheckoutPanel` handles both cases by dropping
 * the row.
 */
export enum SessionCheckoutRuleType {
  DELIVERY_DATES = 'DELIVERY_DATES',
  PAYMENT_METHODS = 'PAYMENT_METHODS'
}

/**
 * The same two literals live on `CheckoutRules` in `interfaces/config.ts`,
 * and these lines are what stop the two drifting apart.
 *
 * Both names are needed. This enum types what the *session* carries back;
 * `CheckoutRules` types the `ruleType` **argument** every checkout-rule
 * operation takes, alongside the camelCase validation rules that share that
 * argument. Neither is a superset of the other, so collapsing them would mean
 * offering `cartConditionPendingMinQty` as a session selection or
 * `DELIVERY_DATES` as a cart validation rule.
 *
 * The cost of two names is that a rename on one side compiles. These
 * assignments close that: each direction fails if the literals stop matching,
 * naming the member that moved. Type-only, so nothing reaches the bundle.
 */
type SessionRuleTypeMatchesConfig =
  `${SessionCheckoutRuleType}` extends
    | `${CheckoutRules.DELIVERY_DATES}`
    | `${CheckoutRules.PAYMENT_METHODS}`
    ? true
    : never
type ConfigRuleTypeMatchesSession = `${
  | CheckoutRules.DELIVERY_DATES
  | CheckoutRules.PAYMENT_METHODS}` extends `${SessionCheckoutRuleType}`
  ? true
  : never

const sessionRuleTypeMatchesConfig: SessionRuleTypeMatchesConfig = true
const configRuleTypeMatchesSession: ConfigRuleTypeMatchesSession = true
void sessionRuleTypeMatchesConfig
void configRuleTypeMatchesSession

/**
 * The runtime shape of the JSON that `SessionCheckoutRule.value` carries.
 *
 * ⚠️ **`value` is a JSON scalar on the wire.** The query selects `value`
 * with no subselection because the schema declares it as GraphQL `JSON` —
 * `value { data amount }` is a `FieldsOnCorrectType` validation error and
 * breaks the whole session bootstrap on every storefront that has the
 * `checkoutRules` field, since `sessionFieldsQuery` is interpolated into
 * three documents (the boot query and both `getInitialValues` queries).
 *
 * The interface below documents the *runtime* shape cinnamon actually
 * reads. The server copies the offered option onto the pick (guide §2),
 * so a `PAYMENT_METHODS` pick carries `{ data: 'CASH' | 'CREDIT' | … }`
 * plus the authored `amount` when the option named one; a `DELIVERY_DATES`
 * pick carries `{ data: 'YYYY-MM-DD' }` and no amount. Every field stays
 * optional so a shape the backend adds before cinnamon's config catches up
 * deserialises as-is.
 */
export interface SessionCheckoutRuleValue {
  /**
   * The selection payload as the storefront wrote it. For `DELIVERY_DATES`
   * a `YYYY-MM-DD` calendar-day string; for `PAYMENT_METHODS` a backend
   * enum value the tenant's config maps onto a shopper-facing label
   */
  data?: string | null
  /**
   * Optional monetary amount attached to the selection. Copied from the
   * offered option onto the pick by the server (guide §2), so present on
   * a `PAYMENT_METHODS` pick when the option carried one; absent on
   * `DELIVERY_DATES`
   */
  amount?: number | null
  /**
   * Keyed-list disambiguator the server copied from the offered option
   * (guide §2). Present on a `PAYMENT_METHODS` list that carries several
   * options with the same `data` (e.g. two `CREDIT` entries at different
   * terms); absent on unkeyed lists and on `DELIVERY_DATES`. Readers that
   * match a pick against the current offered list must compare on
   * `value.key ?? value.data` — matching on `data` alone treats a saved
   * `CREDIT_60D` as offered whenever any `CREDIT` is offered.
   */
  key?: string | null
  /**
   * Display data block the server copied from the offered option at pick
   * time (guide §2). Same shape as `ICheckoutRuleAttributes` on the
   * offered-option side. `selectRulePickLabel` reads `label` from here
   * when the shopper made no pick in this page load (COM-1712), so a
   * reload keeps the keyed copy without a second `getCheckoutRule`.
   */
  attributes?: ICheckoutRuleAttributes | null
  /**
   * Origin tag the server attached to the persisted pick (guide §2).
   * Opaque to cinnamon — carried for forward-compatibility so a backend
   * that starts tagging picks does not need a type bump here first.
   */
  origin?: string | null
}

export interface SessionCheckoutRule {
  ruleType: `${SessionCheckoutRuleType}` | (string & {})
  value?: SessionCheckoutRuleValue | null
}

/**
 * The SCS config bundle a session resolved. Written only on flag-on sessions
 * that resolved a bundle; `null` on flag-off, no-bundle and every resolution
 * failure. A subdocument, so absent hydrates to `null`, unlike the
 * nested-path `configuration.order`.
 */
export interface SessionConfigBundle {
  bundleId?: string | null
}

export interface SessionDataArgs {
  id?: string
  customerUid?: string
  /** The session's customer code, sent to SCS verbatim */
  customerAlias?: string | null
  configBundle?: SessionConfigBundle | null
  workflow?: {
    name?: string
    channel?: `${WorkflowChannel}`
    channelUid?: string
    userUid?: string
    workflowName?: string
  }
  configuration?: SessionConfiguration
  /**
   * The shopper's checkout-rule selections, as a sibling of `configuration`
   * — never as a member of `configuration.checkoutRules`. See
   * `SessionCheckoutRule` for the confusion this comment prevents
   */
  checkoutRules?: SessionCheckoutRule[]
  banners?: Banner[]
  customer?: Customer
  extraIds?: extraId[]
  ownerInfo?: {
    slug: string | null
  }
}

export interface ISession {
  session: SessionDataArgs
}
